import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Assignment as AssignmentIcon,
  AttachMoney as AttachMoneyIcon,
  Attachment as AttachmentIcon,
  LockOutlined as LockOutlinedIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  esRolAdministrador,
  puedeEditarProgramaContabilidad,
  puedeVerModuloContabilidad,
  tienePermisosModulo,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, StatCard, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';
import PrintListado from 'utils/PrintListado';
import {
  btnOutlinedSx,
  btnPrimarySx,
  cardShellSx,
  cellHeadSx,
  fechaLocalHoy,
  filterHeaderSx,
  fmtMoney,
  primerDiaMesActual,
} from './estadosFinancierosShared';

export const PROGRAMA_ANEXO_DECLARACION = 'anexodeclaracion';
export const PROGRAMA_ESTADOS_FINANCIEROS = 'estadosfinancieros';

const TIPO_ANEXO = 'anexo';
const TIPO_DECLARACION = 'declaracion';

export const textoCatalogo = (item) => {
  if (!item) return '';
  if (typeof item === 'string') return item.trim();
  return String(item.descripcion || '').trim();
};

export const normalizarClave = (valor) => String(valor || '').trim().toLowerCase();

export const compararPosicion = (a, b) => {
  const ta = String(a ?? '').trim();
  const tb = String(b ?? '').trim();
  const vaciaA = !ta || ta === '(sin posición)';
  const vaciaB = !tb || tb === '(sin posición)';
  if (vaciaA && vaciaB) return 0;
  if (vaciaA) return 1;
  if (vaciaB) return -1;
  const na = Number(ta);
  const nb = Number(tb);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return ta.localeCompare(tb, 'es', { numeric: true, sensitivity: 'base' });
};

export const filtrarDefinicionesPorAnexo = (definiciones, anexo) => {
  const clave = normalizarClave(anexo);
  if (!clave) return [];
  return (definiciones || []).filter((d) => normalizarClave(d.anexo) === clave);
};

export const filtrarDefinicionesPorDeclaracion = (definiciones, declaracion) => {
  const clave = normalizarClave(declaracion);
  if (!clave) return [];
  return (definiciones || []).filter((d) => normalizarClave(d.declaracion) === clave);
};

const etiquetaPosicion = (valor) => {
  const t = String(valor || '').trim();
  return t || '(sin posición)';
};

const redondear = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;

export const etiquetaCuenta = (cuenta) => {
  const codigo = String(cuenta?.cuenta || '').trim();
  const nombre = String(cuenta?.nombre || '').trim();
  if (codigo && nombre) return `${codigo} — ${nombre}`;
  return codigo || nombre || '';
};

export const cuentasDeDefinicion = (cuentas, definicionId) => {
  if (definicionId == null || definicionId === '') return [];
  const id = String(definicionId);
  return (cuentas || []).filter((c) => String(c.iddefinicionfiscal ?? '') === id);
};

export const cuentasDeDefiniciones = (cuentas, definiciones) => {
  const ids = new Set(
    (definiciones || [])
      .map((d) => (d?.id == null ? '' : String(d.id)))
      .filter(Boolean)
  );
  if (!ids.size) return [];
  return (cuentas || []).filter(
    (c) => c?.iddefinicionfiscal != null && ids.has(String(c.iddefinicionfiscal))
  );
};

/** Saldo del período al estilo DGII: deudor menos acreedor. */
export const montoMovimiento = (mov) => {
  if (!mov) return 0;
  if (mov.saldo != null && mov.saldo !== '') return redondear(mov.saldo);
  const deudor = parseFloat(mov.saldo_deudor) || 0;
  const acreedor = parseFloat(mov.saldo_acreedor) || 0;
  if (deudor || acreedor) return redondear(deudor - acreedor);
  return redondear((parseFloat(mov.debe) || 0) - (parseFloat(mov.haber) || 0));
};

export const mapaMontosPorCuenta = (movimientos) => {
  const mapa = new Map();
  (movimientos || []).forEach((m) => {
    const codigo = String(m?.cuenta || '').trim();
    if (!codigo) return;
    mapa.set(codigo, redondear((mapa.get(codigo) || 0) + montoMovimiento(m)));
  });
  return mapa;
};

/** Extrae el valor de una definición sumando las cuentas vinculadas (casilla DGII). */
export const extraerValorDefinicion = (definicion, cuentas, montosPorCuenta) => {
  const vinculadas = cuentasDeDefinicion(cuentas, definicion?.id);
  if (!vinculadas.length) return 0;
  return redondear(
    vinculadas.reduce((acc, cta) => {
      const codigo = String(cta.cuenta || '').trim();
      return acc + (montosPorCuenta.get(codigo) || 0);
    }, 0)
  );
};

const ordenarItemsAnexo = (items) =>
  items.slice().sort((x, y) => {
    const dec = String(x.declaracion || '').localeCompare(String(y.declaracion || ''), 'es', {
      sensitivity: 'base',
    });
    if (dec !== 0) return dec;
    return compararPosicion(x.posiciondeclaracion, y.posiciondeclaracion);
  });

const ordenarItemsDeclaracion = (items) =>
  items.slice().sort((x, y) => {
    const anexo = String(x.anexo || '').localeCompare(String(y.anexo || ''), 'es', {
      numeric: true,
      sensitivity: 'base',
    });
    if (anexo !== 0) return anexo;
    return compararPosicion(x.posicion, y.posicion);
  });

/**
 * Extrae casillas DGII: anexo por columna o declaración uniendo anexos
 * por posición de declaración, cada una con su valor.
 */
export const extraerCasillasDgii = ({
  tipo,
  definiciones,
  cuentas,
  movimientos,
}) => {
  const montosPorCuenta = mapaMontosPorCuenta(movimientos);
  const items = (definiciones || []).map((d) => ({
    ...d,
    valor: extraerValorDefinicion(d, cuentas, montosPorCuenta),
    cuentas: cuentasDeDefinicion(cuentas, d.id),
  }));

  const mapa = new Map();
  items.forEach((item) => {
    const posicion = etiquetaPosicion(
      tipo === TIPO_DECLARACION ? item.posiciondeclaracion : item.posicion
    );
    if (!mapa.has(posicion)) mapa.set(posicion, []);
    mapa.get(posicion).push(item);
  });

  return [...mapa.entries()]
    .sort((a, b) => compararPosicion(a[0], b[0]))
    .map(([posicion, filas]) => {
      const ordenadas =
        tipo === TIPO_DECLARACION ? ordenarItemsDeclaracion(filas) : ordenarItemsAnexo(filas);
      const total = redondear(ordenadas.reduce((acc, f) => acc + (f.valor || 0), 0));
      const anexos = [...new Set(ordenadas.map((f) => String(f.anexo || '').trim()).filter(Boolean))];
      return { posicion, items: ordenadas, total, anexos };
    });
};

const AnexoDeclaracionFiscal = () => {
  const { controller, selectedCompany, showError, showSuccess, showWarning } =
    useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles, roleWeb } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer =
    esRolAdministrador(roleWeb) ||
    puedeVerModuloContabilidad(roleDetalles, roleWeb) ||
    puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA_ANEXO_DECLARACION) ||
    tienePermisosModulo(roleDetalles, PROGRAMA_ANEXO_DECLARACION) ||
    tienePermisosModulo(roleDetalles, PROGRAMA_ESTADOS_FINANCIEROS);
  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog && claveLog);
  const puedeConsultar = Boolean(puedeLeer && puedeOperar);

  const [tipo, setTipo] = useState(TIPO_ANEXO);
  const [seleccion, setSeleccion] = useState(null);
  const [fechaDesde, setFechaDesde] = useState(primerDiaMesActual);
  const [fechaHasta, setFechaHasta] = useState(fechaLocalHoy);
  const [anexos, setAnexos] = useState([]);
  const [declaraciones, setDeclaraciones] = useState([]);
  const [definiciones, setDefiniciones] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [cargandoCatalogos, setCargandoCatalogos] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [consultado, setConsultado] = useState(false);

  const credenciales = useCallback(
    () => ({
      id_empresa: selectedCompany,
      usuario: usuarioLog || '',
      password: claveLog || '',
    }),
    [selectedCompany, usuarioLog, claveLog]
  );

  const postJson = useCallback(
    async (ruta, extra = {}) => {
      const response = await fetch(`${servidor}${ruta}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...credenciales(), ...extra }),
      });
      const data = await response.json().catch(() => ({}));
      return { ok: response.ok, data };
    },
    [servidor, credenciales]
  );

  const cargarCatalogos = useCallback(async () => {
    if (!puedeConsultar) return;
    setCargandoCatalogos(true);
    try {
      const [resAnexos, resDeclaraciones] = await Promise.all([
        postJson('/contabilidadbackend_listaranexofiscal'),
        postJson('/contabilidadbackend_listardeclaracionfiscal'),
      ]);
      setAnexos(resAnexos.ok && Array.isArray(resAnexos.data.anexos) ? resAnexos.data.anexos : []);
      setDeclaraciones(
        resDeclaraciones.ok && Array.isArray(resDeclaraciones.data.declaraciones)
          ? resDeclaraciones.data.declaraciones
          : []
      );
      if (!resAnexos.ok) {
        showError(resAnexos.data.Error || 'No se pudieron cargar los anexos');
      }
      if (!resDeclaraciones.ok) {
        showError(resDeclaraciones.data.Error || 'No se pudieron cargar las declaraciones');
      }
    } catch {
      setAnexos([]);
      setDeclaraciones([]);
      showError('Error de conexión al cargar anexos y declaraciones');
    } finally {
      setCargandoCatalogos(false);
    }
  }, [puedeConsultar, postJson, showError]);

  useEffect(() => {
    if (puedeConsultar) {
      cargarCatalogos();
    }
  }, [puedeConsultar, selectedCompany?.db_empresa]);

  const opciones = tipo === TIPO_ANEXO ? anexos : declaraciones;

  const limpiarResultado = () => {
    setDefiniciones([]);
    setCuentas([]);
    setMovimientos([]);
    setConsultado(false);
  };

  const cambiarTipo = (siguiente) => {
    setTipo(siguiente);
    setSeleccion(null);
    limpiarResultado();
  };

  const consultar = useCallback(async () => {
    if (!puedeLeer) return;
    if (!usuarioLog) {
      showError('Debe iniciar sesión para consultar');
      return;
    }
    if (!puedeOperar) {
      showError('Seleccione una empresa con base de datos y sesión válida');
      return;
    }
    const etiqueta = textoCatalogo(seleccion);
    if (!etiqueta) {
      showWarning(
        tipo === TIPO_ANEXO
          ? 'Seleccione un anexo registrado'
          : 'Seleccione una declaración registrada'
      );
      return;
    }
    if (!fechaDesde || !fechaHasta) {
      showWarning('Indique fecha desde y fecha hasta');
      return;
    }
    if (fechaDesde > fechaHasta) {
      showWarning('La fecha desde no puede ser mayor que la fecha hasta');
      return;
    }

    setCargando(true);
    try {
      const [resDef, resCuentas, resBalanza] = await Promise.all([
        postJson('/contabilidadbackend_listardefinicionfiscal'),
        postJson('/contabilidadbackend_listarcatalogo'),
        postJson('/contabilidadbackend_balanzadecomprobacion', {
          fecha_desde: fechaDesde,
          fecha_hasta: fechaHasta,
        }),
      ]);
      if (!resDef.ok) {
        limpiarResultado();
        setConsultado(true);
        showError(resDef.data.Error || 'No se pudieron cargar las definiciones fiscales');
        return;
      }
      const todas = Array.isArray(resDef.data.definiciones) ? resDef.data.definiciones : [];
      const filtradas =
        tipo === TIPO_ANEXO
          ? filtrarDefinicionesPorAnexo(todas, etiqueta)
          : filtrarDefinicionesPorDeclaracion(todas, etiqueta);
      const catalogo = resCuentas.ok && Array.isArray(resCuentas.data.cuentas)
        ? resCuentas.data.cuentas
        : [];
      const movs = resBalanza.ok && Array.isArray(resBalanza.data.lineas)
        ? resBalanza.data.lineas.filter((l) => l && l.cuenta && !l.es_titulo)
        : [];
      setDefiniciones(filtradas);
      setCuentas(catalogo);
      setMovimientos(movs);
      setConsultado(true);
      showSuccess(
        filtradas.length
          ? `Valores extraídos · ${filtradas.length} casilla(s)`
          : 'No hay definiciones fiscales para esa selección'
      );
    } catch {
      limpiarResultado();
      setConsultado(true);
      showError('Error de conexión al extraer los valores fiscales');
    } finally {
      setCargando(false);
    }
  }, [
    puedeLeer,
    usuarioLog,
    puedeOperar,
    seleccion,
    tipo,
    fechaDesde,
    fechaHasta,
    postJson,
    showError,
    showWarning,
    showSuccess,
  ]);

  const casillas = useMemo(
    () =>
      extraerCasillasDgii({
        tipo,
        definiciones,
        cuentas,
        movimientos,
      }),
    [tipo, definiciones, cuentas, movimientos]
  );

  const totalGeneral = useMemo(
    () => redondear(casillas.reduce((acc, g) => acc + (g.total || 0), 0)),
    [casillas]
  );

  const cuentasVinculadas = useMemo(
    () => cuentasDeDefiniciones(cuentas, definiciones),
    [cuentas, definiciones]
  );

  const anexosQueAportan = useMemo(() => {
    const set = new Set(definiciones.map((d) => String(d.anexo || '').trim()).filter(Boolean));
    return set.size;
  }, [definiciones]);

  const filasPrint = useMemo(
    () =>
      casillas.flatMap((grupo) => [
        ...grupo.items.map((item) => ({
          posicion_grupo: grupo.posicion,
          anexo: item.anexo || '',
          posicion_anexo: item.posicion || '',
          declaracion: item.declaracion || '',
          posicion_declaracion: item.posiciondeclaracion || '',
          documento: item.documento || '',
          descripcion: item.descripcion || '',
          cuentas: (item.cuentas || []).map(etiquetaCuenta).join(', '),
          valor: fmtMoney(item.valor),
        })),
        {
          posicion_grupo: grupo.posicion,
          anexo: '',
          posicion_anexo: '',
          declaracion: '',
          posicion_declaracion: '',
          documento: '',
          descripcion: 'Total casilla',
          cuentas: '',
          valor: fmtMoney(grupo.total),
        },
      ]),
    [casillas]
  );

  const columnasPrint = useMemo(() => {
    const valor = { id: 'valor', label: 'Valor', align: 'right' };
    if (tipo === TIPO_DECLARACION) {
      return [
        { id: 'posicion_grupo', label: 'Pos. declaración' },
        { id: 'anexo', label: 'Anexo' },
        { id: 'posicion_anexo', label: 'Pos. anexo' },
        { id: 'documento', label: 'Documento' },
        { id: 'descripcion', label: 'Descripción' },
        { id: 'cuentas', label: 'Cuentas' },
        valor,
      ];
    }
    return [
      { id: 'posicion_grupo', label: 'Pos. anexo' },
      { id: 'declaracion', label: 'Declaración' },
      { id: 'posicion_declaracion', label: 'Pos. declaración' },
      { id: 'documento', label: 'Documento' },
      { id: 'descripcion', label: 'Descripción' },
      { id: 'cuentas', label: 'Cuentas' },
      valor,
    ];
  }, [tipo]);

  const nombreEmpresa =
    selectedCompany?.name || selectedCompany?.empresa || nombreNegocio || 'Empresa';
  const tituloConsulta =
    tipo === TIPO_ANEXO
      ? `Anexo ${textoCatalogo(seleccion) || ''}`.trim()
      : `Declaración ${textoCatalogo(seleccion) || ''}`.trim();

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Anexo y Declaración"
          subtitle={nombreNegocio ? `${nombreNegocio} · Consulta fiscal DGII` : 'Consulta fiscal DGII'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso de Contabilidad, «estadosfinancieros» o «anexodeclaracion» en roles
              para acceder a esta consulta.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Anexo y Declaración"
        subtitle={
          nombreNegocio ? `${nombreNegocio} · Extracción fiscal DGII` : 'Extracción fiscal DGII'
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }} id="printable-anexo-declaracion">
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccountTreeIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Selección fiscal
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Box className="no-print" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <PrintListado
                title={nombreEmpresa}
                reportTitle={tituloConsulta || 'Anexo y Declaración'}
                subtitle={`${fechaDesde || ''} — ${fechaHasta || ''} · Valores extraídos como casillas DGII`}
                columns={columnasPrint}
                data={filasPrint}
                emptyMessage="No hay valores para imprimir"
                printButtonLabel="Imprimir"
                printButtonProps={{
                  disabled: filasPrint.length === 0 || !puedeConsultar,
                  sx: btnPrimarySx,
                }}
                footer={`Total extraído: ${fmtMoney(totalGeneral)} · ${casillas.length} casilla(s)`}
              />
              <Button
                variant="contained"
                startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                onClick={consultar}
                disabled={cargando || !puedeConsultar || !textoCatalogo(seleccion)}
                sx={btnPrimarySx}
              >
                Extraer valores
              </Button>
            </Box>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }} className="no-print">
            <Grid container spacing={2.5} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Consultar por"
                  value={tipo}
                  onChange={(e) => cambiarTipo(e.target.value)}
                  disabled={!puedeConsultar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  <MenuItem value={TIPO_ANEXO}>Anexo</MenuItem>
                  <MenuItem value={TIPO_DECLARACION}>Declaración</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={opciones}
                  loading={cargandoCatalogos}
                  value={seleccion}
                  onChange={(_, v) => {
                    setSeleccion(v);
                    limpiarResultado();
                  }}
                  getOptionLabel={textoCatalogo}
                  isOptionEqualToValue={(a, b) => String(a?.id ?? '') === String(b?.id ?? '')}
                  disabled={!puedeConsultar}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={tipo === TIPO_ANEXO ? 'Anexo' : 'Declaración'}
                      placeholder={
                        tipo === TIPO_ANEXO
                          ? 'Buscar anexo (606, 607…)'
                          : 'Buscar declaración (IT-1…)'
                      }
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha desde"
                  type="date"
                  size="small"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!puedeConsultar}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha hasta"
                  type="date"
                  size="small"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!puedeConsultar}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  startIcon={tipo === TIPO_ANEXO ? <AttachmentIcon /> : <AssignmentIcon />}
                  onClick={cargarCatalogos}
                  disabled={cargandoCatalogos || !puedeConsultar}
                  sx={btnOutlinedSx}
                >
                  Actualizar catálogo
                </Button>
              </Grid>
            </Grid>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', mt: 1.5 }}>
              {tipo === TIPO_DECLARACION
                ? 'La declaración extrae los anexos y los suma por posición de declaración, como las casillas de la DGII (IT-1).'
                : 'El anexo extrae el valor de cada posición (columna) y muestra a qué casilla de declaración aporta.'}
            </Typography>
          </CardContent>
        </Card>

        {consultado && !cargando ? (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Casillas"
                value={String(casillas.length)}
                icon={AccountTreeIcon}
                accent={FP.color.accentBlue}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label={tipo === TIPO_DECLARACION ? 'Anexos que aportan' : 'Definiciones'}
                value={String(tipo === TIPO_DECLARACION ? anexosQueAportan : definiciones.length)}
                icon={tipo === TIPO_DECLARACION ? AttachmentIcon : AssignmentIcon}
                accent={FP.color.navy}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Cuentas vinculadas"
                value={String(cuentasVinculadas.length)}
                icon={AccountTreeIcon}
                accent={FP.color.successDark}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Total extraído"
                value={fmtMoney(totalGeneral)}
                icon={AttachMoneyIcon}
                accent={FP.color.navyDeep}
              />
            </Grid>
          </Grid>
        ) : null}

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.2,
                  bgcolor: FP.color.accentBlueSoft,
                  color: FP.color.accentBlue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {tipo === TIPO_ANEXO ? (
                  <AttachmentIcon sx={{ fontSize: 18 }} />
                ) : (
                  <AssignmentIcon sx={{ fontSize: 18 }} />
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
                  {consultado ? tituloConsulta || 'Valores extraídos' : 'Valores extraídos'}
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                  {cargando
                    ? 'Extrayendo valores…'
                    : consultado
                      ? `${casillas.length} casilla(s) · Total ${fmtMoney(totalGeneral)}`
                      : 'Seleccione anexo o declaración, el período y pulse Extraer valores'}
                </Typography>
              </Box>
            </Box>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {cargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
              </Box>
            ) : !consultado || casillas.length === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={8}
              >
                <AccountTreeIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography
                  sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }}
                  textAlign="center"
                  px={2}
                >
                  {consultado
                    ? 'No hay definiciones fiscales para esa selección. Revise el catálogo Definición Fiscal.'
                    : 'Elija un anexo o una declaración y pulse «Extraer valores».'}
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
              >
                <Table size="small" sx={{ minWidth: 1080 }}>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          {tipo === TIPO_DECLARACION ? 'Pos. declaración' : 'Pos. anexo'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          {tipo === TIPO_DECLARACION ? 'Anexo' : 'Declaración'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          {tipo === TIPO_DECLARACION ? 'Pos. anexo' : 'Pos. declaración'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Documento
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Descripción
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Cuentas
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          component="span"
                          sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}
                        >
                          Valor
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {casillas.map((grupo) => (
                      <React.Fragment key={`casilla-${grupo.posicion}`}>
                        {grupo.items.map((item, idx) => (
                          <TableRow key={`${grupo.posicion}-${item.id || idx}`} hover>
                            <TableCell sx={{ fontWeight: idx === 0 ? 700 : 400 }}>
                              {idx === 0 ? grupo.posicion : ''}
                            </TableCell>
                            <TableCell>
                              {tipo === TIPO_DECLARACION ? item.anexo : item.declaracion}
                            </TableCell>
                            <TableCell>
                              {tipo === TIPO_DECLARACION
                                ? item.posicion
                                : item.posiciondeclaracion}
                            </TableCell>
                            <TableCell>{item.documento}</TableCell>
                            <TableCell>{item.descripcion}</TableCell>
                            <TableCell>
                              {(item.cuentas || []).length
                                ? item.cuentas.map(etiquetaCuenta).join(', ')
                                : '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {fmtMoney(item.valor)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow
                          sx={{
                            bgcolor: FP.color.surfaceAlt,
                            '& td': { fontWeight: 700, borderBottom: `2px solid ${FP.color.border}` },
                          }}
                        >
                          <TableCell colSpan={6}>
                            Total casilla {grupo.posicion}
                            {tipo === TIPO_DECLARACION && grupo.anexos.length
                              ? ` · anexos ${grupo.anexos.join(', ')}`
                              : ''}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {fmtMoney(grupo.total)}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AnexoDeclaracionFiscal;
